import { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Paper, Button, FormControl, InputLabel, Select, MenuItem,
  Card, CardContent, Grid, CircularProgress, Alert, Tooltip, Chip, Stack
} from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { getEquipments } from "../api/equipment";
import api from "../api/api";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

const MAX_RANGE_DAYS = 7;
const MAX_RANGE_MS = MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;

const PERFIS = {
  HABITAT: ["CO2", "pressao", "Temp", "Umid", "PM1", "PM2_5", "PM10", "VOC", "NOx"],
  MED: ["CO2", "pressao", "Temp", "Umid", "PM1", "PM2_5", "PM10", "VOC_ppm", "HCHO"],
  PRO: ["Temp", "Umid", "PM1", "PM2_5", "PM10", "CO", "H2S", "VOC", "NOx"],
};

const LABEL_PERFIL = {
  HABITAT: "Sense Habitat",
  MED: "Sense Med",
  PRO: "Sense Pro",
};

// ===================== Parâmetros BASE =====================
const parametrosBase = [
  { key: "CO2", label: "CO₂", limite: 1000, unidade: "ppm" },
  { key: "pressao", label: "Pressão", unidade: "hPa" },
  { key: "Temp", label: "Temperatura", unidade: "°C" },
  { key: "Umid", label: "Umidade", unidade: "%" },
  { key: "PM1", label: "MP1", unidade: "µg/m³" },
  { key: "PM2_5", label: "MP2.5", unidade: "µg/m³" },
  { key: "PM10", label: "MP10", unidade: "µg/m³" },

  // MED
  { key: "VOC_ppm", label: "VOC (ppm)", unidade: "ppm" },
  { key: "HCHO", label: "Formaldeído", unidade: "mg/m³" },

  // Habitat / Pro
  { key: "VOC", label: "VOC (index)", unidade: "index" },
  { key: "NOx", label: "NOx", unidade: "index" },

  // Pro
  { key: "CO", label: "CO", limite: 9, unidade: "ppm" },
  { key: "H2S", label: "H₂S", limite: 8, unidade: "ppm" },
];

function ellipsis(text, max = 18) {
  return text && text.length > max ? text.slice(0, max - 3) + "..." : text;
}

function utcToLocalLabel(dtStr) {
  if (!dtStr) return "";
  let clean = String(dtStr).replace("Z", "").replace(".000", "").trim();

  let datePart = "", timePart = "";
  if (clean.includes("T")) [datePart, timePart] = clean.split("T");
  else if (clean.includes(" ")) [datePart, timePart] = clean.split(" ");
  else return "";

  if (!datePart || !timePart) return "";
  const [y, m, d] = datePart.split("-");
  const [hh = 0, mm = 0, ss = 0] = (timePart || "").split(":");

  const dt = new Date(Date.UTC(+y, +m - 1, +d, +hh, +mm, +ss));
  if (isNaN(dt.getTime())) return "";

  return dt.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).replace(",", "");
}

function normSensor(raw) {
  const s = (raw || "")
    .toString()
    .trim()
    .replace(/\./g, "_")
    .toUpperCase();

  if (s === "PM2_5") return "PM2_5";
  if (s === "VOC_PPM") return "VOC_ppm";
  if (s === "PRESSAO") return "pressao";
  return s;
}

function matchSensor(uiKey, sensorNorm) {
  if (uiKey === "PM2_5") return sensorNorm === "PM2_5";
  if (uiKey === "VOC_ppm") return sensorNorm === "VOC_ppm" || sensorNorm === "VOC";
  if (uiKey === "VOC") return sensorNorm === "VOC";
  if (uiKey === "NOx") return sensorNorm === "NOX";
  if (uiKey === "HCHO") return sensorNorm === "HCHO";
  if (uiKey === "pressao") return sensorNorm === "pressao" || sensorNorm === "PRESSAO";
  if (uiKey === "CO") return sensorNorm === "CO" || sensorNorm === "CO_PPM";
  if (uiKey === "H2S") return sensorNorm === "H2S" || sensorNorm === "H2S_PPM";
  return sensorNorm === uiKey.toUpperCase();
}

function detectarPerfilPorDados(all = {}) {
  if (all.HCHO != null) return "MED";
  if (all.CO != null || all.H2S != null || all.CO_PPM != null || all.H2S_PPM != null) return "PRO";
  return "HABITAT";
}

function hasValue(v) {
  return v != null && Number.isFinite(Number(v));
}

function parametrosPermitidos(profile) {
  return new Set(PERFIS[profile] || PERFIS.HABITAT);
}

function montarParametrosAtivos(profile, all = {}) {
  const permitidos = parametrosPermitidos(profile);

  return parametrosBase.filter((p) => {
    if (!permitidos.has(p.key)) return false;

    if (p.key === "VOC_ppm") {
      return hasValue(all.VOC_ppm) || (profile === "MED" && hasValue(all.VOC));
    }

    if (p.key === "VOC") return hasValue(all.VOC);
    if (p.key === "NOx") return hasValue(all.NOx);
    if (p.key === "HCHO") return hasValue(all.HCHO);
    if (p.key === "PM2_5") return hasValue(all.PM2_5) || hasValue(all["PM2.5"]);
    if (p.key === "CO") return hasValue(all.CO) || hasValue(all.CO_PPM);
    if (p.key === "H2S") return hasValue(all.H2S) || hasValue(all.H2S_PPM);

    return hasValue(all[p.key]);
  });
}

function fallbackParam(profile, ativos) {
  if (ativos?.length) return ativos[0].key;
  if (profile === "PRO") return "CO";
  return "CO2";
}

export default function Estatisticas() {
  const [equipments, setEquipments] = useState([]);
  const [mac, setMac] = useState("");
  const [param, setParam] = useState("CO2");

  const [dataIni, setDataIni] = useState(() => {
    const now = new Date();
    now.setDate(now.getDate() - 1);
    now.setHours(0, 0, 0, 0);
    return now;
  });
  const [dataFim, setDataFim] = useState(() => {
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return now;
  });

  const [estat, setEstat] = useState(null);
  const [serie, setSerie] = useState([]);
  const [diario, setDiario] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [profile, setProfile] = useState("HABITAT");
  const [parametrosAtivos, setParametrosAtivos] = useState(parametrosBase.filter(p => PERFIS.HABITAT.includes(p.key)));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const intervaloMs = useMemo(() => {
    if (!(dataIni instanceof Date) || !(dataFim instanceof Date)) return 0;
    return dataFim.getTime() - dataIni.getTime();
  }, [dataIni, dataFim]);

  const intervaloInvalido = intervaloMs < 0;
  const excedeLimite7Dias = intervaloMs > MAX_RANGE_MS;

  useEffect(() => {
    getEquipments().then((list) => {
      setEquipments(list);
      setMac((prev) => prev || (list.length ? list[0].mac : ""));
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!mac) return;

      try {
        let nextProfile = "HABITAT";

        try {
          const capsRes = await api.get("/equipment/capabilities");
          const atual = (capsRes.data || []).find((item) => item.mac === mac);
          if (atual?.profile) nextProfile = atual.profile;
        } catch {
          // fallback abaixo
        }

        const res = await api.get("/data");
        const all = { ...(res.data?.[mac] || {}) };

        if (all["PM2.5"] != null && all["PM2_5"] == null) all["PM2_5"] = all["PM2.5"];
        if (all.pressao == null && all.Pressao != null) all.pressao = all.Pressao;
        if (all.CO == null && all.CO_PPM != null) all.CO = all.CO_PPM;
        if (all.H2S == null && all.H2S_PPM != null) all.H2S = all.H2S_PPM;

        if (!nextProfile || nextProfile === "HABITAT") {
          nextProfile = detectarPerfilPorDados(all);
        }

        const ativos = montarParametrosAtivos(nextProfile, all);
        const finalAtivos = ativos.length
          ? ativos
          : parametrosBase.filter((p) => parametrosPermitidos(nextProfile).has(p.key));

        setProfile(nextProfile);
        setParametrosAtivos(finalAtivos);

        if (!finalAtivos.find((a) => a.key === param)) {
          setParam(fallbackParam(nextProfile, finalAtivos));
        }
      } catch {
        const fallbackProfile = "HABITAT";
        const fallbackAtivos = parametrosBase.filter((p) => PERFIS[fallbackProfile].includes(p.key));
        setProfile(fallbackProfile);
        setParametrosAtivos(fallbackAtivos);
        if (!fallbackAtivos.find((a) => a.key === param)) {
          setParam(fallbackParam(fallbackProfile, fallbackAtivos));
        }
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mac]);

  useEffect(() => {
    if (intervaloInvalido) {
      setErro("A data final deve ser maior que a data inicial.");
      setEstat(null);
      setSerie([]);
      setDiario([]);
      return;
    }

    if (excedeLimite7Dias) {
      setErro("Para evitar sobrecarga e estouro de renderização, a análise em Estatísticas está limitada a 7 dias por consulta.");
      setEstat(null);
      setSerie([]);
      setDiario([]);
      return;
    }

    setErro((prev) => {
      if (
        prev === "A data final deve ser maior que a data inicial." ||
        prev === "Para evitar sobrecarga e estouro de renderização, a análise em Estatísticas está limitada a 7 dias por consulta."
      ) {
        return "";
      }
      return prev;
    });
  }, [intervaloInvalido, excedeLimite7Dias]);

  const buscarEstatisticas = async () => {
    setErro("");
    setEstat(null);
    setSerie([]);
    setDiario([]);

    if (!mac || !param) return;

    if (intervaloInvalido) {
      setErro("A data final deve ser maior que a data inicial.");
      return;
    }

    if (excedeLimite7Dias) {
      setErro("Para evitar sobrecarga e estouro de renderização, a análise em Estatísticas está limitada a 7 dias por consulta.");
      return;
    }

    setLoading(true);

    try {
      const start_date = dataIni.toISOString().slice(0, 19).replace("T", " ");
      const end_date = dataFim.toISOString().slice(0, 19).replace("T", " ");

      const res = await api.get("/historic-data", {
        params: { mac, start_date, end_date },
      });

      const rows = res.data || [];

      const filtered = rows.filter((r) => {
        const sensorNorm = normSensor(r.sensor);
        return matchSensor(param, sensorNorm);
      });

      const valores = filtered.map((r) => parseFloat(r.value)).filter(Number.isFinite);

      const n = valores.length;
      const media = n ? valores.reduce((a, b) => a + b, 0) / n : null;
      const max = n ? Math.max(...valores) : null;
      const min = n ? Math.min(...valores) : null;
      const desvio = n ? Math.sqrt(valores.reduce((s, v) => s + Math.pow(v - media, 2), 0) / n) : null;

      const parametroInfo = (parametrosBase.find((p) => p.key === param) || {});
      const lim = parametroInfo.limite;
      const percentualAcima = lim && n ? (valores.filter((v) => v > lim).length / n) * 100 : null;

      setEstat({ media, max, min, desvio, percentualAcima, n });

      setSerie(filtered.map((r) => ({
        x: utcToLocalLabel(r.timestamp),
        y: parseFloat(r.value)
      })));

      const grupos = {};
      filtered.forEach((r) => {
        const t = (r.timestamp || "").toString();
        let clean = t.replace("Z", "").replace(".000", "").trim();
        let datePart = "", timePart = "";
        if (clean.includes("T")) [datePart, timePart] = clean.split("T");
        else if (clean.includes(" ")) [datePart, timePart] = clean.split(" ");
        else return;

        const [y, m, d] = datePart.split("-");
        const [hh = 0, mm = 0, ss = 0] = (timePart || "").split(":");
        const dt = new Date(Date.UTC(+y, +m - 1, +d, +hh, +mm, +ss));
        if (isNaN(dt.getTime())) return;

        dt.setUTCHours(dt.getUTCHours() - 3);
        const dia = dt.toISOString().slice(0, 10);

        if (!grupos[dia]) grupos[dia] = [];
        const v = parseFloat(r.value);
        if (Number.isFinite(v)) grupos[dia].push(v);
      });

      const barras = Object.entries(grupos)
        .map(([dia, arr]) => ({
          dia,
          media: arr.reduce((a, b) => a + b, 0) / arr.length,
          max: Math.max(...arr),
          min: Math.min(...arr)
        }))
        .sort((a, b) => a.dia.localeCompare(b.dia));

      setDiario(barras);
    } catch {
      setErro("Não foi possível obter estatísticas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mac && param && dataIni && dataFim && !intervaloInvalido && !excedeLimite7Dias) {
      buscarEstatisticas();
    }
    // eslint-disable-next-line
  }, [mac, param, dataIni, dataFim]);

  const parametroInfo = useMemo(
    () => parametrosBase.find((p) => p.key === param) || {},
    [param]
  );

  function exportarExcelSerie() {
    if (!serie.length) return;
    const ws = XLSX.utils.json_to_sheet(
      serie.map((item) => ({
        "Data/Hora": item.x,
        [parametroInfo.label || param]: item.y
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SerieTemporal");
    const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `serie_temporal_${param}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  }

  function exportarExcelDiario() {
    if (!diario.length) return;
    const ws = XLSX.utils.json_to_sheet(
      diario.map((item) => ({
        "Dia": item.dia.split("-").reverse().join("/"),
        "Média": item.media,
        "Máximo": item.max,
        "Mínimo": item.min,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "EstatisticasDiarias");
    const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `estatisticas_diarias_${param}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  }

  const doughnutData = useMemo(() => {
    if (!estat || !parametroInfo.limite) return null;
    const acima = estat.percentualAcima ?? 0;
    return {
      labels: ["Acima do limite", "Dentro do limite"],
      datasets: [{
        data: [acima, 100 - acima],
        backgroundColor: ["#ff7043", "#26a69a"]
      }]
    };
  }, [estat, parametroInfo.limite]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1250, margin: "0 auto" }}>
      <Typography variant={isMobile ? "h6" : "h4"} gutterBottom>
        Estatísticas dos Dados
      </Typography>

      <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 3 }}>
        <Box sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 2 },
          alignItems: "center",
          mb: 2
        }}>
          <FormControl sx={{ minWidth: 140, flex: 1, maxWidth: 260 }}>
            <InputLabel>Equipamento</InputLabel>
            <Select
              value={mac}
              label="Equipamento"
              onChange={e => setMac(e.target.value)}
              size={isMobile ? "small" : "medium"}
            >
              {equipments.map((e) => (
                <MenuItem key={e.mac} value={e.mac}>
                  {ellipsis(e.location || e.mac, 22)} ({ellipsis(e.mac, 10)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 140, flex: 1, maxWidth: 220 }}>
            <InputLabel>Parâmetro</InputLabel>
            <Select
              value={param}
              label="Parâmetro"
              onChange={e => setParam(e.target.value)}
              size={isMobile ? "small" : "medium"}
            >
              {parametrosAtivos.map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DateTimePicker
            label="Data/Hora Inicial"
            value={dataIni}
            onChange={setDataIni}
            sx={{ minWidth: 110, maxWidth: 180 }}
            slotProps={{ textField: { size: isMobile ? "small" : "medium" } }}
          />

          <DateTimePicker
            label="Data/Hora Final"
            value={dataFim}
            onChange={setDataFim}
            sx={{ minWidth: 110, maxWidth: 180 }}
            slotProps={{ textField: { size: isMobile ? "small" : "medium" } }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={buscarEstatisticas}
            disabled={loading || excedeLimite7Dias || intervaloInvalido}
            sx={{ minWidth: 160, height: 46, fontWeight: 600 }}
            size={isMobile ? "small" : "medium"}
          >
            Calcular Estatísticas
          </Button>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Chip
              size="small"
              label={`Perfil: ${LABEL_PERFIL[profile] || profile}`}
              color={profile === "PRO" ? "warning" : profile === "MED" ? "success" : "info"}
              variant="outlined"
            />
            <Chip
              size="small"
              label="Janela máxima: 7 dias"
              color={excedeLimite7Dias ? "error" : "default"}
              variant="outlined"
            />
          </Stack>
        </Box>

        {excedeLimite7Dias && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Para evitar sobrecarga de renderização e travamentos, a página de Estatísticas aceita no máximo 7 dias por consulta.
          </Alert>
        )}
      </Paper>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : estat ? (
        <>
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              color="success"
              onClick={exportarExcelSerie}
              disabled={!serie.length}
              size={isMobile ? "small" : "medium"}
            >
              Exportar Série Temporal (.xlsx)
            </Button>
            <Button
              variant="outlined"
              color="info"
              onClick={exportarExcelDiario}
              disabled={!diario.length}
              size={isMobile ? "small" : "medium"}
            >
              Exportar Estatísticas Diárias (.xlsx)
            </Button>
          </Box>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card variant="outlined"><CardContent>
                <Typography variant="subtitle2">Média</Typography>
                <Typography variant="h5">{estat.media?.toFixed(2) ?? "-"}</Typography>
                <Typography color="textSecondary" fontSize={12}>{parametroInfo.unidade}</Typography>
              </CardContent></Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card variant="outlined"><CardContent>
                <Typography variant="subtitle2">Máximo</Typography>
                <Typography variant="h5">{estat.max?.toFixed(2) ?? "-"}</Typography>
                <Typography color="textSecondary" fontSize={12}>{parametroInfo.unidade}</Typography>
              </CardContent></Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card variant="outlined"><CardContent>
                <Typography variant="subtitle2">Mínimo</Typography>
                <Typography variant="h5">{estat.min?.toFixed(2) ?? "-"}</Typography>
                <Typography color="textSecondary" fontSize={12}>{parametroInfo.unidade}</Typography>
              </CardContent></Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card variant="outlined"><CardContent>
                <Typography variant="subtitle2">Desvio Padrão</Typography>
                <Typography variant="h5">{estat.desvio?.toFixed(2) ?? "-"}</Typography>
                <Typography color="textSecondary" fontSize={12}>{parametroInfo.unidade}</Typography>
              </CardContent></Card>
            </Grid>

            {parametroInfo.limite && (
              <Grid item xs={12} sm={6} md={2.4}>
                <Card variant="outlined"><CardContent>
                  <Tooltip title={`% dos registros acima do limite de ${parametroInfo.limite} ${parametroInfo.unidade}`}>
                    <Typography variant="subtitle2">% Acima do Limite</Typography>
                  </Tooltip>
                  <Typography variant="h5">{estat.percentualAcima?.toFixed(1) ?? "-"}%</Typography>
                </CardContent></Card>
              </Grid>
            )}
          </Grid>

          <Paper sx={{ p: 2, mb: 2, width: "100%", overflowX: "auto" }}>
            <Typography variant="h6" mb={1}>Série Temporal</Typography>
            {serie.length ? (
              <Line
                data={{
                  labels: serie.map((p) => p.x),
                  datasets: [{
                    label: parametroInfo.label || param,
                    data: serie.map((p) => p.y),
                    fill: false,
                    borderColor: "teal",
                    tension: 0.25,
                    pointRadius: 2,
                  }]
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { title: { display: true, text: "Data/Hora (Local)" } },
                    y: { title: { display: true, text: parametroInfo.unidade || "" } }
                  }
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">Sem dados para o período.</Typography>
            )}
          </Paper>

          {diario.length > 1 && (
            <Paper sx={{ p: 2, mb: 2, width: "100%", overflowX: "auto" }}>
              <Typography variant="h6" mb={1}>Comparativo por Dia</Typography>
              <Bar
                data={{
                  labels: diario.map((p) => p.dia.split("-").reverse().join("/")),
                  datasets: [
                    { label: "Média", data: diario.map((p) => p.media), backgroundColor: "#26a69a" },
                    { label: "Máximo", data: diario.map((p) => p.max), backgroundColor: "#ff7043" },
                    { label: "Mínimo", data: diario.map((p) => p.min), backgroundColor: "#90caf9" }
                  ]
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "top" } },
                  scales: {
                    x: { title: { display: true, text: "Dia" } },
                    y: { title: { display: true, text: parametroInfo.unidade || "" } }
                  }
                }}
              />
            </Paper>
          )}

          {doughnutData && (
            <Paper sx={{ p: 2, mb: 2, maxWidth: 420 }}>
              <Typography variant="h6" mb={1}>Distribuição dos Registros</Typography>
              <Doughnut
                data={doughnutData}
                options={{ plugins: { legend: { position: "bottom" } } }}
              />
            </Paper>
          )}
        </>
      ) : null}
    </Box>
  );
}
